// THE `onboarding_verdict` STATUS SUB-EVENT — one per setup run, always.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { AiFirstScoreSchema } from '../../entities/ai-first-score';

/**
 * The ONE branch a repo-onboarding pass lands on, for every repo, forever
 * (`docs/idea/repo-onboarding.md`).
 *
 * It lives in the DOMAIN because both ends of the fleet read it and apps never
 * import apps: the BOX cuts it (`apps/runner/src/modes/setup/landing.ts`) and the
 * MOTHERSHIP recognizes a merge of it as the second writer of
 * `repositories.ai_first_at` (`apps/worker/src/babysit/projection-sink.ts`). Two
 * hand-written copies of a branch name is how the merge stops being noticed.
 *
 * Constant rather than task-derived, and that IS the idempotency key: two passes
 * over one repo — a re-dispatch, a reaper reassign, a maintainer re-running
 * `project setup` months later — must converge on the same pull request, and a
 * per-task branch would open a second one every time.
 */
export const ONBOARDING_BRANCH = 'dz/onboarding' as const;

/** Status-event name for a repo-onboarding verdict. One per setup run, always. */
export const ONBOARDING_VERDICT_EVENT = 'onboarding_verdict' as const;

/** Artifacts one verdict may name. A checklist, not an inventory — bounded on purpose. */
export const ONBOARDING_VERDICT_MAX_ARTIFACTS = 64;

/**
 * Commands one proof may name as unprovable. The proof names only failed HEAD rows —
 * `bin/setup` and `bin/check`, and the `bin/stop` run once after them — so the bound
 * sits well above anything that ledger produces; a box claiming more is not
 * describing it.
 */
export const ONBOARDING_PROOF_MAX_UNPROVABLE = 8;
/** One command line, as the ledger ran it. */
export const ONBOARDING_PROOF_COMMAND_MAX = 200;
/** The redacted tail of a failed command's output — enough to name the failure, not a log. */
export const ONBOARDING_PROOF_EXCERPT_MAX = 2_000;

/** What the proof step concluded about the gate. Closed. */
export const OnboardingProofState = {
  /** Every command in the ledger ran green on the box. */
  Proved: 'proved',
  /** The gate ran, and some commands hit a named failure — each listed in `unprovable`. */
  ProvedWithGaps: 'proved_with_gaps',
  /** The gate itself could not be proved — each failing command listed in `unprovable`. */
  Unprovable: 'unprovable',
} as const;

/**
 * The PROOF, orthogonal to the verdict (epic #3756, door 3): the verdict says what
 * the pass WROTE (`ready | gaps | blocked`), the proof says whether the gate it
 * declares actually RAN. A `ready` repo can still be `unprovable` on this box.
 *
 * Its one invariant: `proved` names no unprovable command, and the other two name
 * at least one — each state is a claim, and the list is the ledger behind it.
 * The excerpt is REDACTED by the box before it gets here, like every exec excerpt.
 */
export const OnboardingProofSchema = z
  .object({
    state: z.enum(OnboardingProofState),
    unprovable: z
      .array(
        z.object({
          command: z.string().trim().min(1).max(ONBOARDING_PROOF_COMMAND_MAX),
          excerpt: z.string().max(ONBOARDING_PROOF_EXCERPT_MAX),
        }),
      )
      .max(ONBOARDING_PROOF_MAX_UNPROVABLE),
  })
  .superRefine((proof, ctx) => {
    const proved = proof.state === OnboardingProofState.Proved;
    if (proved === (proof.unprovable.length === 0)) return;
    ctx.addIssue({
      code: 'custom',
      message: proved
        ? '`proved` names no unprovable command — a failed command makes it `proved_with_gaps` or `unprovable`'
        : `\`${proof.state}\` must name the command it could not prove`,
      path: ['unprovable'],
    });
  });
export type OnboardingProof = z.infer<typeof OnboardingProofSchema>;

/** The longest recorded stack `label` (#7587): a stack label is a short phrase, never prose. */
export const RECORDED_STACK_LABEL_MAX_CHARS = 200;
/** The longest recorded stack `check` (#7587): one `bin/check` command line. */
export const RECORDED_STACK_CHECK_MAX_CHARS = 400;

/** One line within `max` characters: no CR, LF, tab or other C0 control character, no DEL. */
const recordedLine = (max: number) =>
  z
    .string()
    .max(max)
    // biome-ignore lint/suspicious/noControlCharactersInRegex: refusing control characters IS the bound
    .refine((s) => !/[\u0000-\u001f\u007f]/.test(s), 'must be one line with no control characters');

/**
 * The stack the setup pass DETECTED, as it rendered the reviewer prompt floor from it
 * (#7587): `label`, `check` and whether the command trio is real. The runner records it on
 * its `onboarding_verdict`; the worker's merge egress reads it back to re-render the floor
 * for the gate-path proof's T3. ONE bound for both ends, so the writer never records a stack
 * the reader refuses. A box is untrusted, and `label` / `check` are the floor's only
 * variable text: single line, bounded, and never cut — a cut fact would render bytes the
 * pass never wrote. Extra keys are stripped, so a newer box's addition never costs a verdict.
 */
export const RecordedStackSchema = z.object({
  label: recordedLine(RECORDED_STACK_LABEL_MAX_CHARS),
  check: recordedLine(RECORDED_STACK_CHECK_MAX_CHARS),
  hasRealCommands: z.boolean(),
});
export type RecordedStack = z.infer<typeof RecordedStackSchema>;

/**
 * `value` as a {@link RecordedStack}, or `null` when it is absent or outside the bounds. The
 * writer calls it to decide whether to record (`null` = omit, never cut); the reader calls
 * it on the stored payload's `stack` (`null` = no renderable floor, so the proof refuses).
 */
export function parseRecordedStack(value: unknown): RecordedStack | null {
  const parsed = RecordedStackSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * What one repo-onboarding pass concluded (`docs/idea/repo-onboarding.md`), as
 * streamed by setup mode (`apps/runner/src/modes/setup/`).
 *
 * The mothership stamps `repositories.ai_first_at` from a **`ready`** verdict —
 * the second of the marker's two automated writers, beside the `dz/onboarding`
 * pull request merging. Without it a mature repo that passes every checklist row
 * would be held forever behind a PR that can never exist, because there was
 * nothing to establish.
 *
 * A DISCRIMINATED union on the wire, for the same reason it is one in the box's
 * own core: `ready` and `blocked` both open no pull request and mean opposite
 * things — one is "we looked and it is fine", the other is "we could not look".
 * A nullable PR number said the same thing for both.
 *
 * Same trust-boundary posture as {@link CapabilitiesDetectedPayloadSchema}: a box
 * is untrusted input and this event feeds a DISPATCH gate (the claim gate holds
 * every `outcome_kind = 'pr'` task on a repo with no marker), so the artifact
 * lists are bounded and the verdict vocabulary is closed.
 *
 * `proof` and `scorecard` (epic #3756) are OPTIONAL and the object is NOT strict,
 * and both are load-bearing for a mixed-version fleet: a box older than them
 * emits neither and still parses here, and a sink older than them strips both
 * as unknown keys and still reads the verdict it always read. Making this
 * `.strict()` would make an old sink refuse every new box's verdict. The epic's
 * rule for the stamp: a `ready` stamps `ai_first_at` only when proven, which the
 * sink reads off `proof`.
 */
export const OnboardingVerdictPayloadSchema = z.object({
  event: z.literal(ONBOARDING_VERDICT_EVENT),
  /** Repo the pass ran on — the stamp's target, resolved against the run's owning account. */
  repo: z.string().trim().min(1).max(200),
  verdict: z.enum(['ready', 'gaps', 'blocked']),
  /** Artifacts this pass WROTE. Present on `gaps`; empty/absent otherwise by construction. */
  established: z
    .array(z.string().trim().min(1).max(200))
    .max(ONBOARDING_VERDICT_MAX_ARTIFACTS)
    .optional(),
  /** Artifacts it found and left byte-for-byte alone. The whole content of a `ready` report. */
  verified: z
    .array(z.string().trim().min(1).max(200))
    .max(ONBOARDING_VERDICT_MAX_ARTIFACTS)
    .optional(),
  /** Present on `blocked` only — the named prerequisite the pass could not satisfy. */
  prerequisite: z.string().trim().min(1).max(64).optional(),
  detail: z.string().max(1_000).optional(),
  note: z.string().max(1_000).optional(),
  /** Whether the declared gate RAN — orthogonal to `verdict`. Absent from boxes older than #3756. */
  proof: OnboardingProofSchema.optional(),
  /** The run's AI-first scorecard; the sink stores it as `repositories.ai_first_score`, latest run wins. */
  scorecard: AiFirstScoreSchema.optional(),
  /**
   * The stack the pass detected (#7587, {@link RecordedStackSchema}). Absent from boxes older
   * than it, and omitted by a box whose facts fall outside the bounds. Present and out of
   * bounds parses as ABSENT (coordinator ruling, 2026-10-30): the stack must never cost the
   * verdict its `ai_first_at` stamp or scorecard, and an absent stack fails safe — the merge
   * egress then refuses the reviewer floor's proof as "recorded no detected stack".
   */
  stack: RecordedStackSchema.optional().catch(undefined),
});
export type OnboardingVerdictPayload = z.infer<typeof OnboardingVerdictPayloadSchema>;
